import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, CheckCircle, Shield, Briefcase, Zap, ArrowRight, ArrowLeft } from 'lucide-react';
import './SubscriptionModal.css';

const planMappings = {
  basic: {
    id: 'basic',
    name: 'Basic',
    monthlyPrice: 10,
    priceIds: { monthly: 'price_basic_month', yearly: 'price_basic_year' },
    features: ['Standard profile features', 'Basic job search', 'View up to 5 courses', 'Community forum'],
    icon: <Briefcase size={24} />
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 20,
    priceIds: { monthly: 'price_pro_month', yearly: 'price_pro_year' },
    features: ['Everything in Basic', 'Priority applications', 'Unlimited courses', 'Direct messaging recruiters'],
    icon: <Zap size={24} />
  }
};

const SubscriptionModal = ({ onClose }) => {
  const [step, setStep] = useState('BUDGET_Q'); // BUDGET_Q, USAGE_Q, PREF_Q, RECOMMENDATION, CHECKOUT, SUCCESS
  
  // Wizard Inputs
  const [budget, setBudget] = useState(null); // 'low', 'flexible'
  const [usage, setUsage] = useState(null); // 'basic', 'advanced'
  const [preference, setPreference] = useState(null); // 'monthly', 'yearly'

  // Computed Outputs
  const [recommendedPlanId, setRecommendedPlanId] = useState('basic');
  const [reasoning, setReasoning] = useState('');

  // Stripe
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentError, setPaymentError] = useState(null);

  const annualDiscount = 10; // 10% from SKILL.md

  const computeRecommendation = (selectedPref) => {
    let plan = 'basic';
    let localReason = '';

    // Logic implementation from SKILL.md rules
    if (budget === 'low') {
      plan = 'basic';
      if (usage === 'advanced') {
        localReason = "Since you have a strict budget but need advanced features, we recommend starting with Basic as a safe option to test the waters.";
      } else {
        localReason = "Basic is perfect for essential tools while keeping your budget low.";
      }
    } else {
      // flexible budget
      if (usage === 'advanced') {
        plan = 'pro';
        localReason = "Since you need advanced features and have a flexible budget, Pro is the absolute best match for your career growth.";
      } else {
        plan = 'basic';
        localReason = "Even with a flexible budget, the Basic plan is more than enough for your essential usage needs.";
      }
    }

    if (selectedPref === 'yearly') {
      localReason += " We've applied the 10% annual discount for maximum savings!";
    }

    setRecommendedPlanId(plan);
    setReasoning(localReason);
    setStep('RECOMMENDATION');
  };

  const getFinalPrice = (plan) => {
    let finalPrice = plan.monthlyPrice;
    if (preference === 'yearly') {
      const yearly = plan.monthlyPrice * 12;
      finalPrice = Math.round(yearly * (1 - annualDiscount / 100));
    }
    return finalPrice;
  };

  // Pre-fetch intent when arriving at Checkout
  useEffect(() => {
    if (step === 'CHECKOUT') {
      const config = planMappings[recommendedPlanId];
      if (!config) return;
      const priceId = config.priceIds[preference];
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      fetch(`${backendUrl}/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch secret');
          return res.json();
        })
        .then((data) => {
          if (data.clientSecret) {
            setClientSecret(data.clientSecret);
          } else {
            setClientSecret('pi_dummy_12345');
          }
        })
        .catch(() => {
          setClientSecret('pi_dummy_12345');
        });
    }
  }, [step, recommendedPlanId, preference]);

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setProcessing(true);
    setPaymentError(null);

    const payload = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: elements.getElement(CardElement) }
    });

    if (payload.error) {
      setPaymentError(`Payment failed: ${payload.error.message}`);
      setProcessing(false);
    } else {
      setProcessing(false);
      setStep('SUCCESS');
    }
  };

  const currentPlan = planMappings[recommendedPlanId] || planMappings.basic;
  const finalPrice = getFinalPrice(currentPlan);

  const CARD_OPTIONS = {
    iconStyle: 'solid',
    style: {
      base: { iconColor: '#c4f0ff', color: '#fff', fontSize: '16px', '::placeholder': { color: '#87bbfd' } },
      invalid: { iconColor: '#ef4444', color: '#ef4444' }
    }
  };

  return (
    <div className="sub-modal-overlay">
      <div className="sub-modal-content">
        <button className="sub-modal-close" onClick={onClose}><X size={24} /></button>
        
        {step === 'BUDGET_Q' && (
          <div className="sub-step-fade-in">
            <h2>Step 1: Your Budget</h2>
            <p className="sub-modal-subtitle">Let's find the right plan for you. What does your budget look like?</p>
            <div className="needs-buttons">
              <button className="needs-card" onClick={() => { setBudget('low'); setStep('USAGE_Q'); }}>
                <h3>Low Budget</h3>
                <p>I want to keep costs as low as absolutely possible right now.</p>
              </button>
              <button className="needs-card" onClick={() => { setBudget('flexible'); setStep('USAGE_Q'); }}>
                <h3>Flexible Budget</h3>
                <p>I'm willing to invest in the right tools to accelerate my career.</p>
              </button>
            </div>
          </div>
        )}

        {step === 'USAGE_Q' && (
          <div className="sub-step-fade-in">
            <button className="inline-back" onClick={() => setStep('BUDGET_Q')}><ArrowLeft size={16}/> Back</button>
            <h2>Step 2: Technical Needs</h2>
            <p className="sub-modal-subtitle">How actively will you be using the platform?</p>
            <div className="needs-buttons">
              <button className="needs-card" onClick={() => { setUsage('basic'); setStep('PREF_Q'); }}>
                <h3>Basic Tools</h3>
                <p>I just want standard profile tools and to browse open jobs.</p>
              </button>
              <button className="needs-card pro-card" onClick={() => { setUsage('advanced'); setStep('PREF_Q'); }}>
                <h3>Advanced Tools</h3>
                <p>I need priority applications, direct messaging, and advanced analytics.</p>
              </button>
            </div>
          </div>
        )}

        {step === 'PREF_Q' && (
          <div className="sub-step-fade-in">
            <button className="inline-back" onClick={() => setStep('USAGE_Q')}><ArrowLeft size={16}/> Back</button>
            <h2>Step 3: Billing Preference</h2>
            <p className="sub-modal-subtitle">Would you rather pay month-to-month, or gain a structural discount?</p>
            <div className="needs-buttons">
              <button className="needs-card" onClick={() => { setPreference('monthly'); computeRecommendation('monthly'); }}>
                <h3>Monthly</h3>
                <p>Standard Month-to-Month pricing. Cancel easily.</p>
              </button>
              <button className="needs-card pro-card" onClick={() => { setPreference('yearly'); computeRecommendation('yearly'); }}>
                <h3>Yearly (Save 10%)</h3>
                <p>Commit to a year and we'll apply an automatic 10% discount.</p>
              </button>
            </div>
          </div>
        )}

        {step === 'RECOMMENDATION' && (
          <div className="sub-step-fade-in">
            <h2>Our Recommendation</h2>
            <div className="ai-insight-box">
              <div className="insight-header">
                <Zap size={18} color="#a855f7" />
                 <span>Assistant Match found!</span>
              </div>
              <p>{reasoning}</p>
            </div>
            
            <div className="sub-plans-grid wizard-result">
                <div className="sub-plan-card recommended">
                  <div className="plan-header-flex">
                    {currentPlan.icon}
                    <h3>{currentPlan.name} Plan</h3>
                  </div>
                  <div className="sub-price">
                    <span className="sub-currency">$</span>{finalPrice}
                    <span className="sub-period">{preference === 'yearly' ? '/yr' : '/mo'}</span>
                  </div>
                  <div className="pill-billing">{preference.toUpperCase()} BILLING</div>
                  <ul className="mt-4">
                    {currentPlan.features.map((f, i) => <li key={i}><CheckCircle size={14} color="#a855f7"/> {f}</li>)}
                  </ul>
                </div>
            </div>
            
            <button className="sub-cta-btn" onClick={() => setStep('CHECKOUT')}>
              Proceed to Check Out <ArrowRight size={18} style={{verticalAlign: 'middle', marginLeft: '5px'}}/>
            </button>
            <button className="sub-back-btn" onClick={() => setStep('PREF_Q')}>Retake Questionnaire</button>
          </div>
        )}

        {step === 'CHECKOUT' && (
          <div className="sub-step-fade-in">
            <h2>Secure Payment</h2>
            <div className="checkout-summary-mini">
              <span>{currentPlan.name} Plan ({preference === 'yearly' ? 'Yearly' : 'Monthly'})</span>
              <span className="summary-price">${finalPrice}.00</span>
            </div>
            
            <form onSubmit={handleCheckoutSubmit} className="sub-payment-form">
              <label>Credit or debit card</label>
              <div className="sub-stripe-element">
                <CardElement options={CARD_OPTIONS} />
              </div>
              {paymentError && <div className="sub-error">{paymentError}</div>}
              
              <button disabled={processing || !stripe || !clientSecret} className="sub-cta-btn stripe-cta">
                {processing ? 'Processing...' : `Pay $${finalPrice}.00 Securely`}
              </button>
            </form>
            
            <div className="sub-secure-trust"><Shield size={14} /> Encrypted by Stripe</div>
            <button className="sub-back-btn" onClick={() => setStep('RECOMMENDATION')}>Back to Recommendation</button>
          </div>
        )}

        {step === 'SUCCESS' && (
          <div className="sub-step-fade-in sub-success-pane">
            <CheckCircle size={60} color="#10b981" />
            <h2>Payment Successful!</h2>
            <p>You are now subscribed to the <strong>{currentPlan.name}</strong> plan! All features have been unlocked for your account.</p>
            <button className="sub-cta-btn" onClick={onClose}>Return to Dashboard</button>
          </div>
        )}

      </div>
    </div>
  );
};

export default SubscriptionModal;
